import type { Event as SentryEvent, EventHint } from '@sentry/react';

type SyncResult = SentryEvent | null;
type HookResult = SyncResult | PromiseLike<SyncResult>;

export type BeforeSendHook = (
  event: SentryEvent,
  hint: EventHint,
) => HookResult;

const isThenable = (value: unknown): value is PromiseLike<SyncResult> =>
  Boolean(value) && typeof (value as PromiseLike<unknown>).then === 'function';

/**
 * `Sentry.init` accepts exactly one `beforeSend`. Every concern that wants a
 * hook (the browser-translation tags, the third-party drop in ENG-2752) would
 * otherwise compete for that single key — and a merge that keeps both keys is
 * not an error, JS silently keeps the last one.
 *
 * Runs hooks in order. The first to return `null` ends the chain, so put
 * enrichers before filters.
 */
export const composeBeforeSend =
  (...hooks: BeforeSendHook[]): BeforeSendHook =>
  (event, hint) => {
    const runFrom = (
      index: number,
      current: SyncResult,
    ): SyncResult | Promise<SyncResult> => {
      if (current === null || index >= hooks.length) return current;

      const next = hooks[index](current, hint);

      if (isThenable(next)) {
        return Promise.resolve(next).then((resolved) =>
          runFrom(index + 1, resolved),
        );
      }

      return runFrom(index + 1, next);
    };

    return runFrom(0, event);
  };
